import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

// context
import DataProvider from './context/DataProvider/DataProvider';

// components
import NioScrollToTop from './components/NioScrollToTop/NioScrollToTop';

// landing page — eager-loaded as the entry route
import Landing from './pages/landing';

// inner pages — lazy-loaded so each route is a separate chunk
const InnerNationalCoffeeCensus = lazy(() => import('./pages/inner-pages/national-coffee-census'));
const InnerContactSolution = lazy(() => import('./pages/inner-pages/contact-solution'));
const InnerPricing = lazy(() => import('./pages/inner-pages/pricing'));
const AboutWeathersafe = lazy(() => import('./pages/inner-pages/about-weathersafe'));
const HowSatellitesHelp = lazy(() => import('./pages/inner-pages/how-satellites-help'));
const UsingTheWeather = lazy(() => import('./pages/inner-pages/using-the-weather'));
const WhereAIMatters = lazy(() => import('./pages/inner-pages/where-ai-matters'));
const ImpactReport = lazy(() => import('./pages/inner-pages/impact-report'));
const SideBenefits = lazy(() => import('./pages/inner-pages/side-benefits'));
const FarmMaps = lazy(() => import('./pages/inner-pages/farm-maps'));
const Structure = lazy(() => import('./pages/inner-pages/structure'));
const AchievableGoals = lazy(() => import('./pages/inner-pages/achievable-goals'));
const Weather = lazy(() => import('./pages/inner-pages/weather'));
const Standards = lazy(() => import('./pages/inner-pages/standards'));
const Processing = lazy(() => import('./pages/inner-pages/processing'));
const ImprovedYields = lazy(() => import('./pages/inner-pages/improved-yields'));
const RiskManagement = lazy(() => import('./pages/inner-pages/risk-management'));
const ClimateResilience = lazy(() => import('./pages/inner-pages/climate-resilience'));
const TripleYields = lazy(() => import('./pages/inner-pages/triple-yields'));
const EarlyWarnings = lazy(() => import('./pages/inner-pages/early-warnings'));
const CoopTraining = lazy(() => import('./pages/inner-pages/coop-training'));
const PurchasingCherry = lazy(() => import('./pages/inner-pages/purchasing-cherry'));
const DryingBedManagement = lazy(() => import('./pages/inner-pages/drying-bed-management'));
const HarvestPlanning = lazy(() => import('./pages/inner-pages/harvest-planning'));
const FarmHealthScores = lazy(() => import('./pages/inner-pages/farm-health-scores'));
const SupplyRisk = lazy(() => import('./pages/inner-pages/supply-risk'));
const Traceability = lazy(() => import('./pages/inner-pages/traceability'));
const BlogIndex = lazy(() => import('./pages/inner-pages/blog'));
const BlogPostSeedlings = lazy(() => import('./pages/inner-pages/blog/most-coffee-seedlings-never-produce'));
const BlogPostOriginTech = lazy(() => import('./pages/inner-pages/blog/origin-technology-and-coffee-yields'));
const BlogPostYieldGap = lazy(() => import('./pages/inner-pages/blog/the-coffee-yield-gap'));
const BlogPostEudr = lazy(() => import('./pages/inner-pages/blog/eudr-due-diligence-coffee'));
const BlogPostSbtiFlag = lazy(() => import('./pages/inner-pages/blog/sbti-flag-coffee'));
const BlogPostFlagRunway = lazy(() => import('./pages/inner-pages/blog/coffee-flag-readiness-runway'));
const BlogPostRainForecast = lazy(() => import('./pages/inner-pages/blog/why-coffee-farms-get-wrong-rain-forecast'));
const BlogPostFishbone = lazy(() => import('./pages/inner-pages/blog/fishbone-system-coffee-water'));
const BlogPostBioInputStocks = lazy(() => import('./pages/inner-pages/blog/coffee-bio-input-stocks'));
const BlogPostPestForecastsFeedbackLoop = lazy(() => import('./pages/inner-pages/blog/coffee-pest-forecasts-feedback-loop'));
const BlogPostYieldCfoDisclosures = lazy(() => import('./pages/inner-pages/blog/coffee-yield-cfo-disclosures'));
const BlogPostBiodiversityMethodologyDecade = lazy(() => import('./pages/inner-pages/blog/coffee-biodiversity-methodology-decade'));
const BlogPostYieldEstimateMonthlyRefresh = lazy(() => import('./pages/inner-pages/blog/coffee-yield-estimate-monthly-refresh'));
const BlogPostNotchingVsStumping = lazy(() => import('./pages/inner-pages/blog/coffee-notching-vs-stumping'));
const BlogPostFortnightlyVisits = lazy(() => import('./pages/inner-pages/blog/every-coffee-farm-every-two-weeks'));
const BlogPostGpsScouts = lazy(() => import('./pages/inner-pages/blog/gps-tagged-scout-visits-coffee'));
const BlogPostFarmLevelRecords = lazy(() => import('./pages/inner-pages/blog/farm-level-coffee-records-compliance'));
const BlogPostFloweringScout = lazy(() => import('./pages/inner-pages/blog/flowering-scout-coffee-yield-forecast'));
const BlogPostScoutingOrchestration = lazy(() => import('./pages/inner-pages/blog/automating-coffee-farm-scouting'));
const BlogPostEnviroReport = lazy(() => import('./pages/inner-pages/blog/origin-enviro-plus-report'));
const BlogPostBananaEucalyptus = lazy(() => import('./pages/inner-pages/blog/gap77-banana-not-eucalyptus'));
const BlogPostDeadSoils = lazy(() => import('./pages/inner-pages/blog/most-coffee-soils-biologically-dead'));
const BlogPostHarvestWindow = lazy(() => import('./pages/inner-pages/blog/coffee-post-rain-harvest-window'));
const BlogPostTeraPreta = lazy(() => import('./pages/inner-pages/blog/coffee-tera-preta-amazonian-soil'));
const BlogPostYieldForecast = lazy(() => import('./pages/inner-pages/blog/coffee-yield-forecast-13-years-ground-truth'));
const InnerAboutGap77 = lazy(() => import('./pages/inner-pages/about-gap-77'));
const InnerWhatisAIandML = lazy(() => import('./pages/inner-pages/what-is-ai-and-ml'));
const InnerCoffeeCropresearch = lazy(() => import('./pages/inner-pages/coffee-crop-research'));
const InnerTerms = lazy(() => import('./pages/inner-pages/terms'));
const InnerAllRights = lazy(() => import('./pages/inner-pages/all-rights'));
const InnerPrivacyPolicy = lazy(() => import('./pages/inner-pages/privacy-policy'));
const EudrPage = lazy(() => import('./pages/inner-pages/eudr'));
const FAQPage = lazy(() => import('./pages/inner-pages/faq'));
const MethodologyPage = lazy(() => import('./pages/inner-pages/methodology'));
const PricingPage = lazy(() => import('./pages/inner-pages/pricing-page'));

// miscellaneous pages
const MiscNotFound = lazy(() => import('./pages/404'));
const MiscLogin = lazy(() => import('./pages/auth/login'));
const MiscSignup = lazy(() => import('./pages/auth/sign-up'));
const MiscResetPassword = lazy(() => import('./pages/auth/reset-password'));
const MiscForgotPassword = lazy(() => import('./pages/auth/forgot-password'));


function AppRoutes() {

  return (
    <Routes>

      {/* landing page */}
      <Route path="/" element={<Landing />} />

      {/* Platform */}
      <Route path='/farm-maps' element={<FarmMaps />} />
      <Route path='/structure' element={<Structure />} />
      <Route path='/achievable-goals' element={<AchievableGoals />} />
      <Route path='/weather' element={<Weather />} />
      <Route path='/standards' element={<Standards />} />
      <Route path='/processing' element={<Processing />} />
      <Route path='/improved-yields' element={<ImprovedYields />} />
      <Route path='/risk-management' element={<RiskManagement />} />
      <Route path='/climate-resilience' element={<ClimateResilience />} />

      {/* Solutions */}
      <Route path='/triple-yields' element={<TripleYields />} />
      <Route path='/early-warnings' element={<EarlyWarnings />} />
      <Route path='/coop-training' element={<CoopTraining />} />
      <Route path='/purchasing-cherry' element={<PurchasingCherry />} />
      <Route path='/drying-bed-management' element={<DryingBedManagement />} />
      <Route path='/harvest-planning' element={<HarvestPlanning />} />
      <Route path='/farm-health-scores' element={<FarmHealthScores />} />
      <Route path='/supply-risk' element={<SupplyRisk />} />
      <Route path='/traceability' element={<Traceability />} />
      <Route path='/national-coffee-census' element={<InnerNationalCoffeeCensus />} />

      {/* About */}
      <Route path='/about-weathersafe' element={<AboutWeathersafe />} />
      <Route path='/about-gap-77' element={<InnerAboutGap77 />} />
      <Route path='/how-satellites-help' element={<HowSatellitesHelp />} />
      <Route path='/using-the-weather' element={<UsingTheWeather />} />
      <Route path='/where-ai-matters' element={<WhereAIMatters />} />
      <Route path='/impact-report' element={<ImpactReport />} />
      <Route path='/side-benefits' element={<SideBenefits />} />

      {/* Core pages */}
      <Route path='/eudr' element={<EudrPage />} />
      <Route path='/faq' element={<FAQPage />} />
      <Route path='/methodology' element={<MethodologyPage />} />
      <Route path='/how-it-works' element={<InnerPricing />} />
      <Route path='/pricing' element={<PricingPage />} />
      <Route path='/contact-us-solution' element={<InnerContactSolution />} />

      {/* Blog & resources */}
      <Route path='/blog' element={<BlogIndex />} />
      <Route path='/blog/most-coffee-seedlings-never-produce' element={<BlogPostSeedlings />} />
      <Route path='/blog/origin-technology-and-coffee-yields' element={<BlogPostOriginTech />} />
      <Route path='/blog/the-coffee-yield-gap' element={<BlogPostYieldGap />} />
      <Route path='/blog/eudr-due-diligence-coffee' element={<BlogPostEudr />} />
      <Route path='/blog/sbti-flag-coffee' element={<BlogPostSbtiFlag />} />
      <Route path='/blog/coffee-flag-readiness-runway' element={<BlogPostFlagRunway />} />
      <Route path='/blog/why-coffee-farms-get-wrong-rain-forecast' element={<BlogPostRainForecast />} />
      <Route path='/blog/fishbone-system-coffee-water' element={<BlogPostFishbone />} />
      <Route path='/blog/coffee-bio-input-stocks' element={<BlogPostBioInputStocks />} />
      <Route path='/blog/coffee-pest-forecasts-feedback-loop' element={<BlogPostPestForecastsFeedbackLoop />} />
      <Route path='/blog/coffee-yield-cfo-disclosures' element={<BlogPostYieldCfoDisclosures />} />
      <Route path='/blog/coffee-biodiversity-methodology-decade' element={<BlogPostBiodiversityMethodologyDecade />} />
      <Route path='/blog/coffee-yield-estimate-monthly-refresh' element={<BlogPostYieldEstimateMonthlyRefresh />} />
      <Route path='/blog/coffee-notching-vs-stumping' element={<BlogPostNotchingVsStumping />} />
      <Route path='/blog/every-coffee-farm-every-two-weeks' element={<BlogPostFortnightlyVisits />} />
      <Route path='/blog/gps-tagged-scout-visits-coffee' element={<BlogPostGpsScouts />} />
      <Route path='/blog/farm-level-coffee-records-compliance' element={<BlogPostFarmLevelRecords />} />
      <Route path='/blog/flowering-scout-coffee-yield-forecast' element={<BlogPostFloweringScout />} />
      <Route path='/blog/automating-coffee-farm-scouting' element={<BlogPostScoutingOrchestration />} />
      <Route path='/blog/origin-enviro-plus-report' element={<BlogPostEnviroReport />} />
      <Route path='/blog/gap77-banana-not-eucalyptus' element={<BlogPostBananaEucalyptus />} />
      <Route path='/blog/most-coffee-soils-biologically-dead' element={<BlogPostDeadSoils />} />
      <Route path='/blog/coffee-post-rain-harvest-window' element={<BlogPostHarvestWindow />} />
      <Route path='/blog/coffee-tera-preta-amazonian-soil' element={<BlogPostTeraPreta />} />
      <Route path='/blog/coffee-yield-forecast-13-years-ground-truth' element={<BlogPostYieldForecast />} />
      <Route path='/what-is-ai-and-ml' element={<InnerWhatisAIandML />} />
      <Route path='/coffee-crop-research' element={<InnerCoffeeCropresearch />} />

      {/* Legal */}
      <Route path='/terms-and-conditions' element={<InnerTerms />} />
      <Route path='/all-rights' element={<InnerAllRights />} />
      <Route path='/privacy-policy' element={<InnerPrivacyPolicy />} />

      {/* Auth */}
      <Route path='/auth/login' element={<MiscLogin />} />
      <Route path='/auth/sign-up' element={<MiscSignup />} />
      <Route path='/auth/reset-password' element={<MiscResetPassword />} />
      <Route path='/auth/forgot-password' element={<MiscForgotPassword />} />

      {/* 404 */}
      <Route path="/404" element={<MiscNotFound />} />
      <Route path="*" element={<MiscNotFound />} />

    </Routes>
  );
}


function App() {

  useEffect(() => {
    document.body.classList.add('nl-body');
  }, []);

  return (
    <HelmetProvider>
      <DataProvider>
        <BrowserRouter>
          <NioScrollToTop />
          <Suspense fallback={<div style={{ minHeight: '60vh' }} />}>
            <AppRoutes />
          </Suspense>
        </BrowserRouter>
      </DataProvider>
    </HelmetProvider>
  );
}



export default App;
